/**
 * Size profiles by viewport width (PRD §7.2, C17). Always CSS pixels, never the user agent.
 */
import { LOCKED } from './config.js';

export const LABEL_HEIGHT = 20; // §9: "Video" / "Advertisements" label row
export const HEADER_HEIGHT = 32; // §8: header strip

/**
 * Pick the profile name for a viewport width.
 * @param {number} width viewport width in CSS px
 * @param {object} profiles config.sizeProfiles
 * @returns {'desktop'|'tablet'|'phone'|'narrow'}
 */
export function pickProfile(width, profiles) {
  const order = ['desktop', 'tablet', 'phone', 'narrow'];
  for (const name of order) {
    const profile = profiles[name];
    if (profile && width >= profile.minWidth) return name;
  }
  return 'narrow';
}

/**
 * In-article player size for a profile (§7.2 table).
 * @param {object} input
 * @param {string} input.profileName
 * @param {object} input.profiles config.sizeProfiles
 * @param {number} input.viewportWidth
 * @param {number} input.viewportHeight
 * @param {number} input.columnWidth width of the article column
 * @returns {[number, number]|null} null for narrow (display only)
 */
export function playerSize({ profileName, profiles, viewportWidth, viewportHeight, columnWidth }) {
  const profile = profiles[profileName];
  if (!profile || !profile.player) return null;
  let width;
  if (Array.isArray(profile.player)) width = Math.min(profile.player[0], columnWidth || profile.player[0]);
  else if (profile.player === 'column-16:9') width = columnWidth;
  else width = Math.min(viewportWidth, columnWidth || viewportWidth); // full-bleed-16:9, never wider than the slot
  let height = Math.round((width * 9) / 16);
  if (profileName === 'phone') {
    // Landscape phones: player height at most 60% of the viewport height.
    if (viewportWidth > viewportHeight && height > viewportHeight * 0.6) {
      height = Math.floor(viewportHeight * 0.6);
      width = Math.round((height * 16) / 9);
    }
    // C17: a narrow phone column (under ~358 px) makes a 16:9 frame shorter than 201 px. Use a 4:3 frame
    // instead (C17 allows it; IMA letterboxes 16:9 ads), no taller than the 250 px reserved phone frame.
    if (height < 201) {
      height = Math.min(Math.floor((width * 3) / 4), 250); // floor both: never wider than the column
      width = Math.floor((height * 4) / 3);
    }
  }
  return [Math.round(width), height];
}

/**
 * Frame and slot heights (§7.2 reserved height, §9 zero CLS).
 * Frame = larger of the 16:9 player and the fallback minimum (250 px phone/narrow, 280 px otherwise).
 * @param {string} profileName
 * @param {[number, number]|null} size player size
 * @returns {{frame:number, header:number, label:number, total:number}}
 */
export function slotHeights(profileName, size) {
  const minFrame = profileName === 'desktop' || profileName === 'tablet' ? 280 : 250;
  const frame = Math.max(minFrame, size ? size[1] : 0);
  const header = size ? HEADER_HEIGHT : 0;
  return { frame, header, label: LABEL_HEIGHT, total: LABEL_HEIGHT + header + frame };
}

/**
 * C17: a video ad may only be requested or shown in a player larger than 240x200,
 * with a 16:9 or 4:3 shape.
 * @param {[number, number]|null} size
 * @returns {boolean}
 */
export function isAdSizeAllowed(size) {
  if (!size) return false;
  const [width, height] = size;
  if (width < LOCKED.minAdPlayerSize[0] || height < LOCKED.minAdPlayerSize[1]) return false;
  const ratio = width / height;
  return Math.abs(ratio - 16 / 9) < 0.03 || Math.abs(ratio - 4 / 3) < 0.03;
}

/**
 * Float size for a profile (§7). Null means the profile does not float.
 * @param {string} profileName
 * @param {object} profiles
 * @returns {[number, number]|null}
 */
export function floatSize(profileName, profiles) {
  const profile = profiles[profileName];
  return profile && Array.isArray(profile.float) ? profile.float.slice() : null;
}

/**
 * GPT size mapping rows for the fallback slot (§7.2): only listed sizes, phones 300x250 only.
 * @param {object} profiles
 * @returns {Array<[[number, number], Array<[number, number]>]>}
 */
export function fallbackSizeMapping(profiles) {
  return [
    [[profiles.desktop.minWidth, 0], profiles.desktop.fallback],
    [[profiles.tablet.minWidth, 0], profiles.tablet.fallback],
    [[0, 0], profiles.phone.fallback],
  ];
}

/**
 * Fixed AdSense unit size for a profile (§3.2 step 3): 336x280 desktop/tablet, else 300x250.
 * @param {string} profileName
 * @returns {[number, number]}
 */
export function adsenseSize(profileName) {
  return profileName === 'desktop' || profileName === 'tablet' ? [336, 280] : [300, 250];
}
